/**
 * Certifier relevance map.
 *
 * Keys   : certifier Organisation Name, must match the "Organization Name"
 *          field in the Airtable Certifying Bodies table exactly.
 * Values : array of applicable Brand Category values (matching Airtable's
 *          Brand.Category field), or null = universal (all categories).
 *
 * Safe defaults
 *   - Certifier not in map      → treated as universal (don't filter)
 *   - Brand category unknown    → treated as applicable  (don't penalise)
 *
 * Update this file when new certifiers are added; no scoring logic changes needed.
 */
export const CERTIFIER_RELEVANCE = {
  // ── Beauty / Personal Care ────────────────────────────────────────────────
  // "Home" added 29 July — see backend/scoring.py, which is authoritative.
  "Leaping Bunny":                              ["Beauty", "Personal Care", "Home"],
  "PETA Cruelty-Free":                          ["Animal Care", "Baby & Maternity Care", "Bath & Body", "Beauty", "Health & Wellness", "Household Care", "Home", "Personal Care"],

  // ── Apparel ───────────────────────────────────────────────────────────────
  "Fossil Free Fashion Scorecard":              ["Apparel"],
  "KnowTheChain (Apparel & Footwear benchmark)": ["Apparel"],
  "KnowTheChain Apparel & Footwear":            ["Apparel"],

  // ── Food & Beverage ───────────────────────────────────────────────────────
  "KnowTheChain (Food & Beverage benchmark)":   ["Food & Beverage"],
  "KnowTheChain Food & Beverage":               ["Food & Beverage"],
  "Rainforest Alliance":                        ["Food & Beverage"],
  "USDA Organic":                               ["Food & Beverage"],
  "Certified Humane":                           ["Food & Beverage"],

  // ── Food & Beverage + Apparel ─────────────────────────────────────────────
  "Fair Trade USA":                             ["Food & Beverage", "Apparel"],
  "Fair Trade International":                   ["Food & Beverage", "Apparel"],

  // ── Universal (null = applicable to all categories) ───────────────────────
  "B Corp Overall":                             null,
  "HRC Corporate Equality Index":               null,
  "CDP Climate Score":                          null,
};

/**
 * Returns true if the certifier is applicable to the given brand category.
 *
 * @param {string|undefined} certifierName  Certifier's Organisation Name
 * @param {string}           brandCategory  Brand's Category field value
 */
export function isCertifierRelevant(certifierName, brandCategory) {
  if (!certifierName) return true;                     // unknown certifier → universal
  const applicable = CERTIFIER_RELEVANCE[certifierName];
  if (applicable === undefined) return true;           // not in map → universal
  if (applicable === null) return true;                // explicitly universal
  if (!brandCategory) return true;                     // unknown category → don't penalise
  return applicable.includes(brandCategory);
}

// ── Domain commitment threshold ───────────────────────────────────────────────

/**
 * Threshold constants for the domain commitment check.
 * All values are on a 0–100 raw scale (normalized_score × 10).
 * Adjust here to tune sensitivity without touching scoring logic.
 */
export const DOMAIN_COMMITMENT_THRESHOLDS = {
  LOW_MAX:  59,   // 0–59  → low commitment  → penalise (0)
  HIGH_MIN: 90,   // 90–100 → high commitment → protect  (null)
  //            60–89 → moderate commitment → protect  (null)
};

/**
 * Determine the commitment level a brand has already demonstrated in a domain,
 * based on OTHER certifier ratings (not the current certifier being evaluated).
 *
 * Used to decide whether a missing opt-in certification for an Enterprise brand
 * should be penalised (0) or excused (null).
 *
 * Commitment levels:
 *   'none'     — no other Certified/Assessed ratings in domain → penalise
 *   'low'      — highest raw score 0–59                       → penalise
 *   'moderate' — highest raw score 60–89                      → protect (null)
 *   'high'     — highest raw score ≥ 90 OR any Certified status → protect (null)
 *
 * @param {Array} otherRatings  Domain ratings EXCLUDING the current certifier's row.
 *                              Only Certified and Assessed rows contribute.
 * @returns {'none'|'low'|'moderate'|'high'}
 */
export function getDomainCommitmentLevel(otherRatings) {
  if (!otherRatings || !otherRatings.length) return "none";

  // Only Certified and Assessed rows count — Not Certified / Unknown are not signals of commitment
  const scoreable = otherRatings.filter(
    r => r.status === "Certified" || r.status === "Assessed"
  );

  if (!scoreable.length) return "none";

  // Certified status alone signals commitment regardless of score
  if (scoreable.some(r => r.status === "Certified")) return "high";

  // Find highest raw score (normalized_score is 0–10; multiply by 10 for 0–100 comparison)
  const highestRaw = Math.max(
    ...scoreable
      .filter(r => r.normalized_score != null)
      .map(r => r.normalized_score * 10)
  );

  if (!isFinite(highestRaw)) return "none"; // Assessed rows with no numeric score

  if (highestRaw >= DOMAIN_COMMITMENT_THRESHOLDS.HIGH_MIN) return "high";
  if (highestRaw >  DOMAIN_COMMITMENT_THRESHOLDS.LOW_MAX)  return "moderate"; // 60–89
  return "low"; // 0–59
}
